import { ListItem, ListItemText, CircularProgress } from "@mui/material";
import Autocomplete from "@mui/material/Autocomplete";
import * as dates from "common/dates";
import TextField from "components/common/TextField";
import React, { useMemo } from "react";
import { useListSubmissionsQuery } from "services/submissionsService";

/**
 * SubmissionSelector
 * - Minimal, reusable selector for admin screens
 * - Fetches submissions scoped to a company
 *
 * Props:
 * - companyId: string (required)
 * - value: submission object or null
 * - onChange: (submissionOrNull) => void (required)
 * - label?: string (default "Select Submission")
 * - sx?: object (optional styles to apply to the input)
 * - filterSubmissions?: (submission) => boolean (optional filter function to apply to submission list)
 */
const SubmissionSelector = ({
  companyId,
  value,
  onChange,
  label = "Select Submission",
  sx: inputSx = {},
  filterSubmissions = () => true,
}) => {
  const { data: submissions, isFetching, isError } = useListSubmissionsQuery(
    {
      impersonateCompanyId: companyId,
    },
    { skip: !companyId }
  );

  const defaultSx = { width: "fit-content", minWidth: "20em", flexShrink: 0 };
  const sx = { ...defaultSx, ...inputSx };

  const sortedSubmissions = useMemo(() => {
    const list = (submissions ?? []).filter(filterSubmissions);
    return [...list].sort((a, b) => {
      const aInsured = a?.insured ?? "";
      const bInsured = b?.insured ?? "";
      return aInsured.localeCompare(bInsured);
    });
  }, [submissions, filterSubmissions]);

  const makeSubmissionLabel = (s) => {
    const inception = dates.formatDate(s?.inception);
    const insured = s?.insured ?? "<Unknown>";
    return inception ? `${insured} (${inception})` : insured;
  };

  const renderPlaceholder = (text) => (
    <TextField label={text} variant={"outlined"} size={"small"} disabled />
  );

  if (!companyId) {
    return renderPlaceholder("Select company first");
  }

  if (isError) {
    return renderPlaceholder("Failed to load submissions");
  }

  // While fetching and no data yet, show a loading field
  if (isFetching && (submissions == null || submissions.length === 0)) {
    return (
      <TextField
        label={`${label} — Loading…`}
        variant={"outlined"}
        size={"small"}
        disabled
        InputProps={{ endAdornment: <CircularProgress size={18} /> }}
        sx={sx}
      />
    );
  }

  return (
    <Autocomplete
      options={sortedSubmissions}
      value={value ?? null}
      onChange={(_, newValue) => {
        onChange?.(newValue ?? null);
      }}
      getOptionLabel={(option) => makeSubmissionLabel(option)}
      isOptionEqualToValue={(option, val) =>
        option?.submissionId === val?.submissionId
      }
      renderOption={(props, option) => (
        <ListItem {...props} key={option?.submissionId}>
          <ListItemText
            primary={makeSubmissionLabel(option)}
            secondary={option?.submissionId}
            sx={{ whiteSpace: "nowrap" }}
          />
        </ListItem>
      )}
      renderInput={(params) => (
        <TextField
          {...params}
          label={label}
          variant={"outlined"}
          size={"small"}
          InputProps={{
            ...params.InputProps,
            endAdornment: (
              <>
                {isFetching ? (
                  <CircularProgress size={18} sx={{ mr: 1 }} />
                ) : null}
                {params.InputProps.endAdornment}
              </>
            ),
          }}
        />
      )}
      loading={isFetching}
      noOptionsText={isFetching ? "Loading…" : "No submissions found"}
      size={"small"}
      sx={sx}
    />
  );
};

export default SubmissionSelector;
